import { useContext, useEffect, useState, useCallback, useMemo } from "react";  // Add useMemo to imports

import {
  CreditCardOutlined,
  LockTwoTone,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
  UploadOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import {
  BuildingOfficeIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import AppContext from "../../AppContext";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, salutations } from "../../Constants";
import { useTranslation } from "react-i18next";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  TicketIcon,
} from "@heroicons/react/20/solid";
import SendConfirmationDialog from "../employee/SendConfirmationDialog";
import CouponValidator from "../coupons/CouponValidator";
 import Cropper from "react-easy-crop";
// import { useCallback } from "react";

// Helpers for cropping
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });

const getCroppedImg = async (imageSrc, cropPixels) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(cropPixels.width));
  canvas.height = Math.max(1, Math.floor(cropPixels.height));
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    Math.floor(cropPixels.x),
    Math.floor(cropPixels.y),
    Math.floor(cropPixels.width),
    Math.floor(cropPixels.height),
    0,
    0,
    canvas.width,
    canvas.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      resolve(blob);
    }, "image/jpeg", 0.9);
  });
};

export default function EmployeeRegistrationForm() {
  const [cropImage, setCropImage] = useState(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropTarget, setCropTarget] = useState(null); // "avatar" or "company"
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const location = useLocation();
  const authCtx = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [registerLoginLoading] = useState(false);  // Changed to const since setter unused
  const { t } = useTranslation();
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    salutation: "",
    gender: "Male",
    role: "",
    employeeId: "",
    company: "",
    arabicSupport: false,
    phone: "",
    firstNameArabic: "",
    lastNameArabic: "",
    trialCoupon: "",
    arabicContent: { firstName: "", lastName: "", role: "", companyName: "" },
    password: "",
    confirmPassword: "",
    id: null,
  });
    const profilePlaceholder = useMemo(() => 
    formData.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png", 
    [formData.gender]
  );
  const revokeIfObjectUrl = useCallback((url) => {
    try {
      if (url && typeof url === "string" && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.debug("Failed to revoke URL", err);
    }
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState("/future_cards_logo.png");
  
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);
  

  const handleCropSave = useCallback(async () => {
    if (!cropImage || !croppedAreaPixels) {
      setShowCropDialog(false);
      setCropImage(null);
      setCropTarget(null);
      return;
    }
    try {
      const blob = await getCroppedImg(cropImage, croppedAreaPixels);
      const file = new File([blob], `img_${Date.now()}.jpg`, { type: blob.type });
      const objectUrl = URL.createObjectURL(file);

      if (cropTarget === "avatar") {
        console.log("Setting avatar preview:", objectUrl);
        revokeIfObjectUrl(avatarPreview);
        setAvatarFile(file);
        setAvatarPreview(objectUrl);
      } else if (cropTarget === "company") {
        console.log("Setting company logo preview:", objectUrl);
        revokeIfObjectUrl(companyLogoPreview);
        setCompanyLogoFile(file);
        setCompanyLogoPreview(objectUrl);
      }

    } catch (err) {
      console.error("Crop failed", err);
      toast.error("Failed to crop image");
    } finally {
      setShowCropDialog(false);
      setCropImage(null);
      setCropTarget(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [cropImage, croppedAreaPixels, cropTarget, avatarPreview, companyLogoPreview, revokeIfObjectUrl]);

  const handleProfileImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    if(!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      e.target.value = null;
      return;
    }
    const fileSizeInKB = file.size / 1024;
    if (fileSizeInKB > 512) {
      toast.error("Image size should be less than 512 KB.");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      console.log("profile file read, opening cropper");
      setCropImage(reader.result);
      setCropTarget("avatar");
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
    e.target.value = null; // reset input
  };
  const handleCompanyLogoChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0]; 

    if(!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      e.target.value = null;
      return;
    }

    const fileSizeInKB = file.size / 1024;
    if (fileSizeInKB > 2048) {
      toast.error("Image size should be less than 2 MB.");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
    console.log("company file read, opening cropper");
    setCropImage(reader.result);
    setCropTarget("company");
    setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
    e.target.value = null; // reset input
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Update avatar placeholder when gender changes (only if no custom image uploaded)
    if (name === "gender" && (!avatarPreview || !avatarPreview.startsWith("blob:"))) {
      const newPlaceholder = value === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png";
      setAvatarPreview(newPlaceholder);
    }
  };

  const handleArabicChange = (e) => {
    const { name, value } = e.target;  // Removed unused type, checked
    setFormData((prevData) => ({
      ...prevData,
      arabicContent: {
        ...prevData.arabicContent,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    authCtx?.logout();
    
    if (validate()) {
      setLoading(true);

      try {
        // Create FormData to send files with the employee data
        const formDataToSend = new FormData();
        
        // Add employee data as JSON
        const employeeData = {
          ...formData,
          designation: formData.role,
        };
        formDataToSend.append('employeeItem', JSON.stringify(employeeData));
        
        // Add files if they exist
        if (avatarFile) {
          formDataToSend.append('profileLogo', avatarFile);
        }
        if (companyLogoFile) {
          formDataToSend.append('companyLogo', companyLogoFile);
        }

        // Send single POST request with all data and files
        const createResp = await axios.post(`${BASE_URL}/PublicEmployee/`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const createdId = createResp?.data?.Id || createResp?.data?.id || createResp?.data?.ID;

        if (!createdId) {
          throw new Error("Failed to create employee");
        }

        setFormData((prev) => ({...prev, id: createdId }));

        // Fetch the created employee to get uploaded file URLs
        const getResp = await axios.get(
          `${BASE_URL}/PublicEmployee/${createdId}`
        );

        if(getResp && getResp.data) {
          const updated  = getResp.data;
          setFormData((prev) => ({
            ...prev,
            profileUrl: updated.ProfileUrl || updated.profileUrl || prev.profileUrl,
            companyIconUrl: updated.CompanyIconUrl || updated.companyIconUrl || prev.companyIconUrl,
            id: createdId,
          }));

          if(updated.profileUrl || updated.ProfileUrl || updated.companyIconUrl || updated.CompanyIconUrl) {
            setAvatarPreview(updated.profileUrl || updated.ProfileUrl || avatarPreview);
            setCompanyLogoPreview(updated.companyIconUrl || updated.CompanyIconUrl || companyLogoPreview);
          }
        }
        
        setEmailSent(true);

      } 
      catch (error) {
        console.error("Error saving employee:", error);
        toast.error("Failed to save employee");
      } 
      finally {
        setLoading(false);
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    const pwd = formData.password || "";
    if (pwd.length < 6) newErrors.password = "Password must be at least 6 characters";
    if ((formData.confirmPassword || "") !== pwd) newErrors.confirm = "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dataParam = params.get("data");
    if (dataParam) {
      try {
        const decodedData = JSON.parse(atob(dataParam));
        setFormData((prev) => ({
          ...prev,
          firstName: decodedData.firstName || "",
          lastName: decodedData.lastName || "",
          email: decodedData.email || "",
          salutation: decodedData.title || "",
          role: decodedData.designation || "",
          employeeId: decodedData.employeeId || "",
          company: decodedData.company || "",
          arabicSupport: decodedData.arabicSupport || false,
          phone: decodedData.phone || "",
          firstNameArabic: decodedData.firstNameArabic || "",
          lastNameArabic: decodedData.lastNameArabic || "",
          password: "",
          confirmPassword: "",
          trialCoupon: prev.trialCoupon || "",
          arabicContent: prev.arabicContent || { firstName: "", lastName: "", role: "", companyName: "" },
        }));
      } catch (error) {
        console.error("Error decoding URL data:", error);
      }
    }
  }, [location.search]);

  async function handleLogin() {
    authCtx?.logout();
    window.location.href = "/";
  }

  if (emailSent) {
    return (
      <div className="min-h-screen flex justify-center items-center absolute top-0 left-0 w-screen bg-gray-100">
        <div className="p-8 h-full bg-white shadow-lg rounded-lg text-center max-w-md">
          <h1 className="text-2xl flex flex-col justify-center font-bold text-green-600">
            <CheckCircleIcon height={270} />
            Email Sent Successfully
          </h1>

          {/* English Text */}
          <div className="mt-6 mb-4">
            <p className="text-gray-700 text-lg leading-relaxed">
              You have received an email to view your card.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mt-2">
              If you want to continue customizing your card and add your profile
              image, please log in.
            </p>
          </div>

          {/* Arabic Text */}
          <div className="mt-4 mb-6 border-t pt-4" dir="rtl">
            <p className="text-gray-700 text-lg leading-relaxed">
              لقد استلمت بريداً إلكترونياً لعرض بطاقتك بنجاح.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mt-2">
              إذا كنت ترغب في الاستمرار بتخصيص بطاقتك وإضافة صورتك الشخصية، يرجى
              تسجيل الدخول.
            </p>
          </div>

          {/* Login Button */}
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-3 px-6 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 mt-4"
          >
            Login / تسجيل الدخول
          </button>
        </div>
      </div>
    );
  }

  const handleCouponApplied = (coupon) => {
    if (coupon) {
      setFormData((prevData) => ({
        ...prevData,
        trialCoupon: coupon.code,
      }));
    }
  };

  return (
    <div className="min-h-screen absolute top-0 left-0 w-full bg-gray-100 flex flex-col">
      {/* Made Arabia Logo - Top Right */}
      <div
        className="absolute"
        style={{ top: 20, right: 20, zIndex: 50 }}
      >
        <img
          src="/madeEng.-Horizontal-cmyk.png"
          alt="Made Arabia Logo"
          style={{ height: "50px", objectFit: "contain", mixBlendMode: "multiply" }}
        />
      </div>

      {showCropDialog && cropImage && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-md max-w-2xl w-full p-4">
            <div style={{ position: "relative", height: 360 }}>
                <Cropper
                  image={cropImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={cropTarget === "company" ? 4 / 3 : 1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
              <button onClick={() => { setShowCropDialog(false); setCropImage(null); }} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleCropSave} className="px-4 py-2 bg-indigo-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
      {/* Professional Saudi National Day Banner - Fixed at top - COMMENTED OUT
      <div
        className="fixed top-0 left-0 w-full text-white py-3 overflow-hidden z-50 shadow-xl"
        style={{ background: "#1B5E20" }}
      >
        <div className="whitespace-nowrap">
          <div className="flex items-center scroll-container">
            <div className="flex items-center mx-20 scroll-content">
              <div className="flex items-center space-x-5">
                <div className="flex items-center space-x-4 bg-white/20 backdrop-blur-sm rounded-xl px-8 py-0 border border-white/30">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🇸🇦</span>
                    <span
                      className="text-base font-saudi tracking-wide"
                      style={{ color: "var(--saudi-white)" }}
                    >
                      Saudi National Day 2025
                    </span>
                  </div>
                </div>

                <div className="w-px h-10 bg-white/40"></div>

                <div className="flex items-center space-x-4">
                  <div
                    className="px-4 py-1 rounded-lg font-saudi-bold text-sm"
                    style={{
                      backgroundColor: "var(--saudi-gold)",
                      color: "var(--saudi-green-dark)",
                    }}
                  >
                    SPECIAL OFFER
                  </div>
                  <span
                    className="text-base font-saudi"
                    style={{ color: "var(--saudi-white)" }}
                  >
                    Use code{" "}
                    <span
                      className="font-saudi-bold px-3 py-1 rounded-lg text-base"
                      style={{
                        backgroundColor: "var(--saudi-gold)",
                        color: "var(--saudi-green-dark)",
                      }}
                    >
                      KSA95
                    </span>{" "}
                    for 365 days FREE
                  </span>
                </div>

                <div className="flex items-center space-x-4 bg-white/20 backdrop-blur-sm rounded-xl px-8 py-1 border border-white/30">
                  <span
                    className="text-base font-saudi"
                    style={{ color: "var(--saudi-white)" }}
                    dir="rtl"
                  >
                    استخدم الرمز KSA95 واحصل على عام كامل مجاناً
                  </span>
                  <span className="text-2xl">🎉</span>
                </div>
              </div>
            </div>

            <div className="flex items-center mx-20 scroll-content">
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-4 bg-white/20 backdrop-blur-sm rounded-xl px-8 py-3 border border-white/30">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🇸🇦</span>
                    <span
                      className="text-base font-saudi tracking-wide"
                      style={{ color: "var(--saudi-white)" }}
                    >
                      Saudi National Day 2025
                    </span>
                  </div>
                </div>

                <div className="w-px h-10 bg-white/40"></div>

                <div className="flex items-center space-x-4">
                  <div
                    className="px-4 py-2 rounded-lg font-saudi-bold text-sm"
                    style={{
                      backgroundColor: "var(--saudi-gold)",
                      color: "var(--saudi-green-dark)",
                    }}
                  >
                    SPECIAL OFFER
                  </div>
                  <span
                    className="text-base font-saudi"
                    style={{ color: "var(--saudi-white)" }}
                  >
                    Use code{" "}
                    <span
                      className="font-saudi-bold px-3 py-2 rounded-lg text-base"
                      style={{
                        backgroundColor: "var(--saudi-gold)",
                        color: "var(--saudi-green-dark)",
                      }}
                    >
                      KSA95
                    </span>{" "}
                    for 365 days FREE
                  </span>
                </div>

                <div className="flex items-center space-x-4 bg-white/20 backdrop-blur-sm rounded-xl px-8 py-3 border border-white/30">
                  <span
                    className="text-base font-saudi"
                    style={{ color: "var(--saudi-white)" }}
                    dir="rtl"
                  >
                    استخدم الرمز KSA95 واحصل على عام كامل مجاناً
                  </span>
                  <span className="text-2xl">🎉</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      */}
      {/* Main content - padding reduced since banner is hidden */}
      <div className="pt-4">
        <SendConfirmationDialog
          showSendDialog={showSendDialog}
          setShowSendDialog={setShowSendDialog}
          employeeData={formData}
          id={formData.id}
        />
        <div className="max-w-md w-full mx-auto mt-12 p-6 bg-white rounded-lg shadow-lg">
          <div className="flex flex-col items-center justify-center bg-indigo-500 p-6 rounded-md shadow-md mb-6">
            <div className="mb-3">
              <img
                alt="Your Company"
                src={"/future_cards_logo.png"}
                className="h-12"
              />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-wide">
              {t("pageTitle.registration")}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col items-center mb-4">
                                    <label className="cursor-pointer group">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleProfileImageChange}
                                            className="hidden"
                                        />
                                        <div className="relative">
                                            <img
                                                src={
                                                  avatarPreview ||
                                                  formData.profileUrl ||
                                                  formData.ProfileUrl ||
                                                  profilePlaceholder
                                                }
                                                alt="Profile"
                                                className="w-28 h-28 rounded-full border-4 border-indigo-200 object-cover shadow-lg"
                                                onError={(e) => (e.target.src = profilePlaceholder)}
                                            />
                                            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <CameraOutlined className="text-white text-2xl" />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 bg-indigo-500 rounded-full p-2 shadow-md border-2 border-white">
                                                <CameraOutlined className="text-white text-sm" />
                                            </div>
                                        </div>
                                    </label>
                                    <p className="text-sm text-indigo-600 mt-2 font-medium">Click to upload photo</p>
                                    <p className="text-xs text-gray-400">Max 512KB • JPG, PNG</p>
                                </div>
           {/* Personal Information Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("form.title")}
                </label>
                <select
                  name="salutation"
                  value={formData.salutation}
                  onChange={handleChange}
                  className="w-full p-2 pr-3 border border-gray-300 rounded-md"
                  required
                >
                  <option value=""> {t("form.select")}</option>
                  {salutations.map((x) => (
                    <option value={x}>{x}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full p-2 pr-3 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <UserOutlined size={16} className="mr-1" />
                    {t("form.firstName")}
                  </div>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <UserOutlined size={16} className="mr-1" />
                    {t("form.lastName")}
                  </div>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <MailOutlined size={16} className="mr-1" />
                    {t("form.email")}
                  </div>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <PhoneOutlined size={16} className="mr-1" />
                    {t("form.phone")}
                  </div>
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* Professional Information Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <BriefcaseIcon height={18} className="mr-1" />
                    {t("form.role")}
                  </div>
                </label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <CreditCardOutlined size={16} className="mr-1" />
                    {t("form.employeeId")}
                  </div>
                </label>
                <input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <BuildingOfficeIcon height={18} className="mr-1" />
                  {t("form.company")}
                </div>
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Company Logo Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Logo
              </label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer group flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCompanyLogoChange}
                    className="hidden"
                  />
                  <div className="relative w-full h-24 bg-gray-50 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-indigo-400 transition-colors">
                    {companyLogoPreview && companyLogoPreview !== "/future_cards_logo.png" ? (
                      <>
                        <img 
                          src={companyLogoPreview} 
                          alt="Company Logo" 
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <CameraOutlined className="text-white text-2xl" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col items-center">
                          <UploadOutlined className="text-3xl text-gray-400" />
                          <div className="text-sm text-gray-500 mt-1">Upload Company Logo</div>
                        </div>
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <CameraOutlined className="text-white text-2xl" />
                        </div>
                      </>
                    )}
                  </div>
                </label>
                {companyLogoFile && (
                  <button
                    type="button"
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                    onClick={() => { 
                      revokeIfObjectUrl(companyLogoPreview); 
                      setCompanyLogoFile(null); 
                      setCompanyLogoPreview("/future_cards_logo.png"); 
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Recommended: PNG/JPEG, max 2MB
              </p>
            </div>

            {/* Arabic Support & Coupon Section */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("form.arabicSupport")}
                </label>
                <div className="flex items-center mt-2">
                  <div className="relative inline-block w-12 mr-2 align-middle">
                    <input
                      type="checkbox"
                      name="arabicSupport"
                      id="arabicSupport"
                      checked={formData.arabicSupport}
                      value={formData.arabicSupport}
                      onChange={handleChange}
                      className="absolute opacity-0 w-full h-full cursor-pointer"
                    />
                    <div
                      className={`block w-10 h-6 rounded-full transition-colors cursor-pointer ${
                        formData.arabicSupport ? "bg-blue-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                          formData.arabicSupport
                            ? "transform translate-x-4"
                            : ""
                        }`}
                      ></div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-600">
                    {formData.arabicSupport ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <TicketIcon height={18} className="mr-1" />
                    {t("form.trialCoupon")}
                  </div>
                </label>
                <CouponValidator
                  onCouponApplied={handleCouponApplied}
                  type="coupon"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("form.trialCouponHint")}
                </p>
              </div>
            </div>

            {/* Arabic Content Section */}
            {formData.arabicSupport && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <UserOutlined size={16} className="mr-1" />
                      {t("form.lastNameArabic")}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.arabicContent?.lastName}
                      onChange={handleArabicChange}
                      className="w-full p-2 border border-gray-300 text-right rounded-md"
                      required={formData.arabicSupport}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <UserOutlined size={16} className="mr-1" />
                      {t("form.firstNameArabic")}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.arabicContent?.firstName}
                      onChange={handleArabicChange}
                      className="w-full p-2 border text-right border-gray-300 rounded-md"
                      required={formData.arabicSupport}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center">
                      <BriefcaseIcon height={18} className="mr-1" />
                      Designation (Arabic)
                    </div>
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.arabicContent?.role}
                    onChange={handleArabicChange}
                    className="w-full p-2 border border-gray-300 text-right rounded-md"
                    required={formData.arabicSupport}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center">
                      <BuildingOfficeIcon height={18} className="mr-1" />
                      Company (Arabic)
                    </div>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.arabicContent?.companyName}
                    onChange={handleArabicChange}
                    className="w-full p-2 border border-gray-300 text-right rounded-md"
                    required={formData.arabicSupport}
                  />
                </div>
              </div>
            )}{" "}
            <div>
              <div className="flex items-center">
                <LockTwoTone height={18} className="mr-1" />
                Password
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className={`w-full bg-transparent border border-gray-300 rounded-md ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-800"
                >
                  {showPassword ? (
                    <EyeSlashIcon height={18} />
                  ) : (
                    <EyeIcon height={18} />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500 mt-1">{errors.password}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <LockTwoTone height={18} className="mr-1" />
                  {t("form.confirmPassword")}
                </div>
              </label>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full bg-transparent border border-gray-300 rounded-md ${
                  errors.confirm ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.confirm && (
                <p className="text-sm text-red-500 mt-1">{errors.confirm}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full py-2 px-4 bg-indigo-500 hover:bg-blue-700 text-white font-medium rounded-md"
              disabled={loading || registerLoginLoading}
            >
              {loading ? (
                <span className="animate-pulse">{t("buttons.saving")}</span>
              ) : (
                <span className="flex gap-2 justify-center">
                  <SaveOutlined />
                  {t("buttons.register")}
                </span>
              )}{" "}
            </button>
            {/* Register & Login Button */}
            <button
              type="button"
              onClick={() => handleLogin()}
              className="w-full py-2 px-4 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-md mt-3"
              disabled={loading || registerLoginLoading}
            >
              Login
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="w-full text-center py-4 text-sm text-gray-500">
          © 2026{" "}
          <a
            href="https://www.future-horses.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-500 hover:underline"
          >
            Future Horses
          </a>
          . All rights reserved.
        </div>
      </div>{" "}
      {/* Close the pt-12 wrapper div */}
    </div>
  );
}
